import className from './invoice.module.scss';
import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Invoice as InvoiceType, SatValidationError } from '../../types/invoice';
import getInvoiceUIState from '../../util/get-invoice-ui-state';
import getInvoiceErrorMessage from '../../util/get-invoice-error-message';
import InvoiceStatus from '../invoice-status/invoice-status';
import Spinner from '../spinner/spinner';
import Button from '../button/button';
import Collapsable from '../collapsable/collapsable';
import SidebarDetail from '../sidebar/sidebar-detail';

interface InvoiceProps {
    invoice: InvoiceType | null;
    isRetrying: boolean;
    onRetry: () => void;
}

export default function Invoice(props: InvoiceProps) {
    const [isSatErrorsExpanded, setIsSatErrorsExpanded] = useState(false);

    const uiState = useMemo(() => getInvoiceUIState(props.invoice), [props.invoice]);

    const renderRetryButton = () => (
        <Button className={className['retry-button']} isLoading={props.isRetrying} onClick={props.onRetry}>
            Reintentar
        </Button>
    );

    const renderSatErrorRow = (error: SatValidationError, index: number) => (
        <div key={index} className={className['sat-error-row']}>
            <div className={className['sat-error-header']}>
                <span className={className['sat-error-code']}>{error.validacion}</span>
                <span className={className['sat-error-meta']}>
                    {error.categoria} — {error.numeral}
                </span>
            </div>
            <div className={className['sat-error-message']}>{error.mensaje_error}</div>
        </div>
    );

    const renderCertificationFields = (invoice: InvoiceType) => (
        <>
            {invoice.authorizationNumber && (
                <div className={className.field}>
                    <div className={className.label}>UUID SAT</div>
                    <div className={className.value}>{invoice.authorizationNumber}</div>
                </div>
            )}
            {invoice.series && invoice.invoiceNumber && (
                <div className={className.field}>
                    <div className={className.label}>Serie / No.</div>
                    <div className={className.value}>
                        {invoice.series}-{invoice.invoiceNumber}
                    </div>
                </div>
            )}
            {invoice.certificationDate && (
                <div className={className.field}>
                    <div className={className.label}>Fecha de certificación</div>
                    <div className={className.value}>
                        {DateTime.fromISO(invoice.certificationDate).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}
                    </div>
                </div>
            )}
        </>
    );

    const renderContent = () => {
        const invoice = props.invoice;

        switch (uiState) {
            case 'no-invoice':
                return <div className={className['no-invoice']}>Sin factura generada</div>;

            case 'processing':
                return (
                    <div className={className.processing}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        <div className={className['processing-label']}>
                            <Spinner className={className.spinner} />
                            Procesando factura…
                        </div>
                    </div>
                );

            case 'certified':
                return (
                    <div className={className.certified}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        {renderCertificationFields(invoice!)}
                    </div>
                );

            case 'cancelled':
                return (
                    <div className={className.cancelled}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        <div className={className['cancelled-note']}>Anulada con nota de crédito</div>
                        {renderCertificationFields(invoice!)}
                    </div>
                );

            case 'rejected':
                return (
                    <div className={className.rejected}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        {invoice!.errorCode && (
                            <div className={className['error-message']}>
                                {getInvoiceErrorMessage(invoice!.errorCode)}
                            </div>
                        )}
                        {invoice!.errorMessage && (
                            <div className={className['error-detail']}>{invoice!.errorMessage}</div>
                        )}
                    </div>
                );

            case 'validation-error-with-details':
                return (
                    <div className={className['validation-error']}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        {invoice!.errorCode && (
                            <div className={className['error-message']}>
                                {getInvoiceErrorMessage(invoice!.errorCode)}
                            </div>
                        )}
                        {invoice!.validationErrors && invoice!.validationErrors.length > 0 && (
                            <>
                                <div
                                    className={className['sat-errors-toggle']}
                                    onClick={() => setIsSatErrorsExpanded(v => !v)}
                                >
                                    {isSatErrorsExpanded ? 'Ocultar' : 'Ver'} detalles SAT (
                                    {invoice!.validationErrors.length})
                                </div>
                                <Collapsable collapsed={!isSatErrorsExpanded}>
                                    <div className={className['sat-errors-list']}>
                                        {invoice!.validationErrors.map(renderSatErrorRow)}
                                    </div>
                                </Collapsable>
                            </>
                        )}
                        {renderRetryButton()}
                    </div>
                );

            case 'contact-support':
                return (
                    <div className={className['contact-support']}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        {invoice!.errorCode && (
                            <div className={className['error-message']}>
                                {getInvoiceErrorMessage(invoice!.errorCode)}
                            </div>
                        )}
                        <div className={className['support-note']}>
                            Contactar a soporte técnico para resolver este problema.
                        </div>
                    </div>
                );

            case 'retry-after-midnight':
                return (
                    <div className={className['retry-after-midnight']}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        <div className={className['error-message']}>
                            Límite diario de FEEL agotado. Reintenta después de la medianoche.
                        </div>
                        {renderRetryButton()}
                    </div>
                );

            case 'validation-error':
                return (
                    <div className={className['validation-error']}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        {invoice!.errorCode && (
                            <div className={className['error-message']}>
                                {getInvoiceErrorMessage(invoice!.errorCode)}
                            </div>
                        )}
                        {invoice!.errorMessage && (
                            <div className={className['error-detail']}>{invoice!.errorMessage}</div>
                        )}
                        {renderRetryButton()}
                    </div>
                );

            case 'failed':
                return (
                    <div className={className.failed}>
                        <InvoiceStatus>{invoice!.status}</InvoiceStatus>
                        {invoice!.errorCode && (
                            <div className={className['error-message']}>
                                {getInvoiceErrorMessage(invoice!.errorCode)}
                            </div>
                        )}
                        {invoice!.errorMessage && (
                            <div className={className['error-detail']}>{invoice!.errorMessage}</div>
                        )}
                        {renderRetryButton()}
                    </div>
                );
        }
    };

    return (
        <SidebarDetail label="Factura FEL">
            <div className={className.base}>{renderContent()}</div>
        </SidebarDetail>
    );
}
